import { useImpersonation } from "@/core/auth/useImpersonation";
import { Button } from "@/shared/ui/button";
import { AlertTriangle, LogOut, Clock, ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useState } from "react";

const ImpersonationBanner = () => {
  const { isImpersonating, targetUserName, targetUserEmail, adminEmail, expiresAt, endImpersonation } = useImpersonation();
  const [timeRemaining, setTimeRemaining] = useState<string>("");
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!expiresAt) return;

    const updateTime = () => {
      const now = new Date();
      const diff = expiresAt.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeRemaining("Scaduta");
        return;
      }

      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  if (!isImpersonating) return null;

  // Collapsed: small floating pill
  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        className="fixed top-2 right-2 z-50 flex items-center gap-1.5 bg-red-600 text-white text-xs font-semibold pl-2.5 pr-2 py-1.5 rounded-full shadow-lg hover:bg-red-700 transition-colors"
      >
        <AlertTriangle className="h-3.5 w-3.5" />
        <span>{targetUserName || targetUserEmail}</span>
        <span className="text-red-200">{timeRemaining}</span>
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
    );
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white shadow-lg">
      <div className="container mx-auto px-4 py-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 animate-pulse" />
              <span className="font-bold text-sm uppercase tracking-wide">
                Modalità Impersonation
              </span>
            </div>

            <div className="hidden sm:flex items-center gap-2 text-sm">
              <span>Stai operando come:</span>
              <span className="font-semibold bg-red-700 px-2 py-0.5 rounded">
                {targetUserName || targetUserEmail}
              </span>
            </div>

            <div className="hidden md:flex items-center gap-2 text-sm text-red-100">
              <span>Admin:</span>
              <span>{adminEmail}</span>
            </div>

            <div className="hidden lg:flex items-center gap-1 text-sm text-red-100">
              <Clock className="h-4 w-4" />
              <span>{timeRemaining}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCollapsed(true)}
              className="h-8 w-8 flex items-center justify-center rounded hover:bg-red-500 transition-colors"
              aria-label="Nascondi banner"
            >
              <ChevronUp className="h-4 w-4" />
            </button>
            <Button
              variant="secondary"
              size="sm"
              onClick={endImpersonation}
              className="bg-white text-red-600 hover:bg-red-50 font-semibold"
            >
              <LogOut className="h-4 w-4 mr-1" />
              Termina Sessione
            </Button>
          </div>
        </div>

        {/* Mobile: show user on second line */}
        <div className="sm:hidden mt-1 text-sm">
          <span>Stai operando come: </span>
          <span className="font-semibold">{targetUserName || targetUserEmail}</span>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
